// CSV-Export/Import-Funktionen für strukturierte Templates

export const exportTemplateToCSV = (template) => {
  const csvHeader = [
    'template_id',
    'template_name',
    'template_type',
    'standard_for_type',
    'standard_for_values',
    'element_type',
    'order',
    'element_id',
    'element_name',
    'subtitle',
    'field_type',
    'database_field',
    'width',
    'height',
    'finding_type',
    'group_id',
    'placeholder',
    'unit',
    'custom_unit',
    'options',
    'comment_placeholder'
  ];

  const csvRows = [];
  
  // Header-Zeile hinzufügen
  csvRows.push(csvHeader.join(','));
  
  // Template-Metadaten (nur erste Zeile)
  const standardFor = template.standard_for || { type: 'all', values: [] };
  const standardForValues = standardFor.values ? standardFor.values.join(',') : '';
  
  const metadataRow = [
    template.id || '',
    `"${template.name || ''}"`,
    `"${template.template_type || 'S'}"`,
    `"${standardFor.type || 'all'}"`,
    `"${standardForValues}"`,
    '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
  ];
  csvRows.push(metadataRow.join(','));
  
  // Template-Elemente verarbeiten
  let order = 1;
  
  // Prüfe ob Template neue einheitliche Struktur verwendet
  const hasUnifiedStructure = template.fields_json && 
    Array.isArray(template.fields_json) && 
    template.fields_json.some(item => item.item_type);
  
  if (hasUnifiedStructure) {
    // Neue einheitliche Struktur - durchlaufe fields_json
    template.fields_json.forEach(item => {
      const row = createCSVRowFromItem(item, order);
      csvRows.push(row.join(','));
      order++;
    });
  } else {
    // Legacy-Struktur für Rückwärtskompatibilität
    
    // Normale Felder (fields_json)
    if (template.fields_json && Array.isArray(template.fields_json)) {
      template.fields_json.forEach(field => {
        const row = createCSVRowFromField(field, order);
        csvRows.push(row.join(','));
        order++;
      });
    }
    
    // Strukturierte Elemente (structured_json)
    let structured_json = template.structured_json;
    if (typeof structured_json === 'string') {
      try {
        structured_json = JSON.parse(structured_json);
      } catch (e) {
        structured_json = { groups: [], findings: [] };
      }
    }
    if (!structured_json) {
      structured_json = { groups: [], findings: [] };
    }
    
    // Solitäre Findings
    if (structured_json.findings) {
      structured_json.findings.forEach(finding => {
        const row = createCSVRowFromFinding(finding, order);
        csvRows.push(row.join(','));
        order++;
      });
    }
    
    // Gruppen mit ihren Findings
    if (structured_json.groups) {
      structured_json.groups.forEach(group => {
        // Gruppe selbst
        const groupRow = createCSVRowFromGroup(group, order);
        csvRows.push(groupRow.join(','));
        order++;
        
        // Findings der Gruppe
        if (group.findings) {
          group.findings.forEach(finding => {
            const row = createCSVRowFromFinding(finding, order, group.id);
            csvRows.push(row.join(','));
            order++;
          });
        }
      });
    }
  }
  
  return csvRows.join('\n');
};

const createCSVRowFromItem = (item, order, templateId = '') => {
  const emptyRow = ['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''];
  
  if (item.item_type === 'field') {
    return createCSVRowFromField(item, order, templateId);
  } else if (item.item_type === 'group') {
    return createCSVRowFromGroup(item, order, templateId);
  } else if (item.item_type === 'finding') {
    return createCSVRowFromFinding(item, order, item.group_id, templateId);
  }
  
  return emptyRow;
};

const createCSVRowFromField = (field, order, templateId = '') => {
  return [
    templateId, '', '', '', '', // Template-ID, rest leer oder Template-Metadaten leer
    'field',
    order,
    `"${field.id || ''}"`,
    `"${field.label || ''}"`,
    '', // subtitle nur bei findings
    `"${field.type || 'freetext'}"`,
    `"${field.database_field || ''}"`,
    field.width || 12,
    field.height || 3,
    '', // finding_type nur bei findings
    '', // group_id nur bei findings
    `"${field.placeholder || ''}"`,
    '', '', '', '' // unit, custom_unit, options, comment_placeholder nur bei findings
  ];
};

const createCSVRowFromGroup = (group, order, templateId = '') => {
  return [
    templateId, '', '', '', '', // Template-ID, rest leer oder Template-Metadaten leer
    'group',
    order,
    `"${group.id || ''}"`,
    `"${group.name || ''}"`,
    '', '', '', '', '', '', '', '', '', '', '', ''
  ];
};

const createCSVRowFromFinding = (finding, order, groupId = null, templateId = '') => {
  const options = finding.options ? finding.options.join(';') : '';
  const unit = finding.unit === 'custom' ? 'custom' : (finding.unit || '');
  
  return [
    templateId, '', '', '', '', // Template-ID, rest leer oder Template-Metadaten leer
    'finding',
    order,
    `"${finding.id || ''}"`,
    `"${finding.name || ''}"`,
    `"${finding.subtitle || ''}"`,
    '', '', '', // field_type, database_field, width, height nur bei fields
    `"${finding.finding_type || 'freetext'}"`,
    `"${groupId || finding.group_id || ''}"`,
    `"${finding.placeholder || ''}"`,
    `"${unit}"`,
    `"${finding.custom_unit || ''}"`,
    `"${options}"`,
    `"${finding.comment_placeholder || ''}"`
  ];
};

export const parseCSVToTemplate = (csvContent) => {
  const lines = csvContent.split('\n').filter(line => line.trim());
  if (lines.length < 2) {
    throw new Error('CSV muss mindestens Header und eine Datenzeile enthalten');
  }
  
  const header = lines[0].split(',').map(col => col.trim().replace(/"/g, ''));
  const rows = lines.slice(1).map(line => parseCSVLine(line));
  
  // Template-Metadaten aus erster Datenzeile
  const metadataRow = rows[0];
  const template = {
    id: metadataRow[0] || null,
    name: metadataRow[1] || 'Importiertes Template',
    template_type: metadataRow[2] || 'S',
    standard_for: {
      type: metadataRow[3] || 'all',
      values: metadataRow[4] ? metadataRow[4].split(',').map(v => parseInt(v.trim())).filter(v => !isNaN(v)) : []
    },
    fields_json: [],
    created_by: 1 // Fallback, wird beim Speichern überschrieben
  };
  
  // Element-Zeilen verarbeiten
  rows.slice(1).forEach(row => {
    const elementType = row[5]; // element_type
    if (!elementType) return;
    
    const baseItem = {
      item_type: elementType,
      id: row[7] || `${elementType}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      order: parseInt(row[6]) || 0
    };
    
    if (elementType === 'field') {
      const field = {
        ...baseItem,
        label: row[8] || '',
        type: row[10] || 'freetext',
        database_field: row[11] || '',
        width: parseInt(row[12]) || 12,
        height: parseInt(row[13]) || 3,
        placeholder: row[16] || ''
      };
      template.fields_json.push(field);
      
    } else if (elementType === 'group') {
      const group = {
        ...baseItem,
        name: row[8] || '',
        findings: []
      };
      template.fields_json.push(group);
      
    } else if (elementType === 'finding') {
      const finding = {
        ...baseItem,
        name: row[8] || '',
        subtitle: row[9] || '',
        finding_type: row[14] || 'freetext',
        group_id: row[15] || null,
        placeholder: row[16] || '',
        unit: row[17] || '',
        custom_unit: row[18] || '',
        options: row[19] ? row[19].split(';').map(opt => opt.trim()).filter(opt => opt) : [],
        comment_placeholder: row[20] || ''
      };
      
      // Finding zu passender Gruppe hinzufügen oder als solitäres Finding
      if (finding.group_id) {
        const group = template.fields_json.find(item => item.item_type === 'group' && item.id === finding.group_id);
        if (group) {
          group.findings.push(finding);
        } else {
          // Gruppe nicht gefunden, als solitäres Finding hinzufügen
          finding.group_id = null;
          template.fields_json.push(finding);
        }
      } else {
        template.fields_json.push(finding);
      }
    }
  });
  
  // Nach Order sortieren
  template.fields_json.sort((a, b) => (a.order || 0) - (b.order || 0));
  
  return template;
};

const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  let i = 0;
  
  while (i < line.length) {
    const char = line[i];
    
    if (char === '"' && !inQuotes) {
      inQuotes = true;
    } else if (char === '"' && inQuotes) {
      if (i + 1 < line.length && line[i + 1] === '"') {
        current += '"';
        i++; // Skip next quote
      } else {
        inQuotes = false;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
    i++;
  }
  
  result.push(current.trim());
  return result;
};

export const exportMultipleTemplatesToCSV = (templates) => {
  const csvHeader = [
    'template_id',
    'template_name',
    'template_type',
    'standard_for_type',
    'standard_for_values',
    'element_type',
    'order',
    'element_id',
    'element_name',
    'subtitle',
    'field_type',
    'database_field',
    'width',
    'height',
    'finding_type',
    'group_id',
    'placeholder',
    'unit',
    'custom_unit',
    'options',
    'comment_placeholder'
  ];

  const csvRows = [];
  
  // Header-Zeile hinzufügen
  csvRows.push(csvHeader.join(','));
  
  // Jedes Template verarbeiten
  templates.forEach(template => {
    // Template-Metadaten-Zeile
    const standardFor = template.standard_for || { type: 'all', values: [] };
    const standardForValues = standardFor.values ? standardFor.values.join(',') : '';
    
    const metadataRow = [
      template.id || '',
      `"${template.name || ''}"`,
      `"${template.template_type || 'S'}"`,
      `"${standardFor.type || 'all'}"`,
      `"${standardForValues}"`,
      '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
    ];
    csvRows.push(metadataRow.join(','));
    
    // Template-Elemente verarbeiten
    let order = 1;
    
    // Prüfe ob Template neue einheitliche Struktur verwendet
    const hasUnifiedStructure = template.fields_json && 
      Array.isArray(template.fields_json) && 
      template.fields_json.some(item => item.item_type);
    
    if (hasUnifiedStructure) {
      // Neue einheitliche Struktur - durchlaufe fields_json
      template.fields_json.forEach(item => {
        const row = createCSVRowFromItem(item, order, template.id);
        csvRows.push(row.join(','));
        order++;
      });
    } else {
      // Legacy-Struktur für Rückwärtskompatibilität
      
      // Normale Felder (fields_json)
      if (template.fields_json && Array.isArray(template.fields_json)) {
        template.fields_json.forEach(field => {
          const row = createCSVRowFromField(field, order, template.id);
          csvRows.push(row.join(','));
          order++;
        });
      }
      
      // Strukturierte Elemente (structured_json)
      let structured_json = template.structured_json;
      if (typeof structured_json === 'string') {
        try {
          structured_json = JSON.parse(structured_json);
        } catch (e) {
          structured_json = { groups: [], findings: [] };
        }
      }
      if (!structured_json) {
        structured_json = { groups: [], findings: [] };
      }
      
      // Solitäre Findings
      if (structured_json.findings) {
        structured_json.findings.forEach(finding => {
          const row = createCSVRowFromFinding(finding, order, null, template.id);
          csvRows.push(row.join(','));
          order++;
        });
      }
      
      // Gruppen mit ihren Findings
      if (structured_json.groups) {
        structured_json.groups.forEach(group => {
          // Gruppe selbst
          const groupRow = createCSVRowFromGroup(group, order, template.id);
          csvRows.push(groupRow.join(','));
          order++;
          
          // Findings der Gruppe
          if (group.findings) {
            group.findings.forEach(finding => {
              const row = createCSVRowFromFinding(finding, order, group.id, template.id);
              csvRows.push(row.join(','));
              order++;
            });
          }
        });
      }
    }
    
    // Leerzeile zwischen Templates für bessere Lesbarkeit
    csvRows.push('');
  });
  
  return csvRows.join('\n');
};

export const validateTemplate = (template) => {
  const errors = [];
  
  if (!template.name || template.name.trim() === '') {
    errors.push('Template-Name ist erforderlich');
  }
  
  if (!template.template_type || !['F', 'S'].includes(template.template_type)) {
    errors.push('Template-Typ muss "F" oder "S" sein');
  }
  
  if (!template.standard_for || !['all', 'modalities', 'examinations'].includes(template.standard_for.type)) {
    errors.push('Standard-für-Typ muss "all", "modalities" oder "examinations" sein');
  }
  
  if (!Array.isArray(template.fields_json)) {
    errors.push('fields_json muss ein Array sein');
  }
  
  // Validiere Element-IDs auf Eindeutigkeit
  const ids = new Set();
  template.fields_json.forEach((item, index) => {
    if (!item.id) {
      errors.push(`Element ${index + 1}: ID ist erforderlich`);
    } else if (ids.has(item.id)) {
      errors.push(`Element ${index + 1}: ID "${item.id}" ist bereits vergeben`);
    } else {
      ids.add(item.id);
    }
    
    if (!item.item_type || !['field', 'group', 'finding'].includes(item.item_type)) {
      errors.push(`Element ${index + 1}: item_type muss "field", "group" oder "finding" sein`);
    }
  });
  
  return errors;
}; 